import React, { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import Drawer from '@mui/material/Drawer';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import InventoryBox from '@mui/icons-material/Inventory2Outlined';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { NavigationItem } from '@converge/graphql';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import { config } from '@converge/config';
import { styled } from '@mui/material/styles';

enum env {
  Dev = 'development',
  Staging = 'staging',
  QA = 'qa',
}

type NavItem = NavigationItem & {
  icon?: React.ReactNode;
  subItems?: Array<NavigationItem & { icon?: React.ReactNode }>;
};

export interface INewLeftNavigationProp {
  items: NavItem[];
  selectedItem: NavItem | null;
  onItemSelected(item: NavItem): void;
  selectedSubItem?: NavItem | null;
  onSubItemSelected?(item: NavItem, parentItem: NavItem): void;
  getItemIcon?: (icon: string) => React.ReactNode;
}

const DRAWER_WIDTH = 312;
const CLOSED_WIDTH = 78;

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'paddingTop',
})<{ open: boolean; paddingTop?: string }>(({ theme, open, paddingTop }) => ({
  width: CLOSED_WIDTH, // Always use CLOSED_WIDTH for the container to prevent layout shifts
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  position: 'fixed',
  left: 0,
  '& .MuiDrawer-paper': {
    top: paddingTop ?? '11.4rem',
    height: `calc(100vh - ${paddingTop ?? '11.4rem'})`,
    position: 'fixed',
    zIndex: 1400,
    left: 0,
    borderRight: 'none',
    overflowY: 'auto',
    overflowX: 'hidden',
    background: '#cefcf6',
    transition: theme.transitions.create(['width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
    width: open ? DRAWER_WIDTH : CLOSED_WIDTH,
    transform: 'none !important',
    visibility: 'visible',
    // Add box shadow when open to create visual separation
    boxShadow: open ? '4px 0 8px rgba(0, 0, 0, 0.1)' : 'none',

    // Hide scrollbar for Chrome, Safari and Opera
    '&::-webkit-scrollbar': {
      display: 'none',
    },

    // Hide scrollbar for IE, Edge and Firefox
    '-ms-overflow-style': 'none', // IE and Edge
    'scrollbar-width': 'none', // Firefox
  },
}));

// Create a wrapper component that will maintain a fixed width
const NavWrapper = styled('div')({
  width: CLOSED_WIDTH,
  flexShrink: 0,
  position: 'relative',
});

export const NewLeftNavigation: React.FC<INewLeftNavigationProp> = ({
  items,
  selectedItem,
  onItemSelected,
  selectedSubItem,
  onSubItemSelected,
  getItemIcon,
}) => {
  // Detect Safari browser immediately for initial state
  const detectSafari = () => {
    if (typeof navigator === 'undefined') return false;

    const ua = navigator.userAgent.toLowerCase();

    // Safari specific checks
    const isSafariDesktop =
      ua.indexOf('safari') !== -1 &&
      ua.indexOf('chrome') === -1 &&
      ua.indexOf('android') === -1;

    // iOS Safari check (all iOS browsers use WebKit)
    const isIOSSafari =
      /iphone|ipad|ipod/.test(ua) &&
      !ua.includes('crios') && // Not Chrome on iOS
      !ua.includes('fxios'); // Not Firefox on iOS

    // Additional WebKit check for older Safari versions
    const isWebKit =
      ua.includes('applewebkit') &&
      !ua.includes('chrome') &&
      !ua.includes('edg'); // Not Edge

    return isSafariDesktop || isIOSSafari || isWebKit;
  };

  const isSafari = detectSafari();

  // Set initial open state to false for all browsers
  const [open, setOpen] = useState(false);
  const [expandedParent, setExpandedParent] = useState<string | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    if (selectedSubItem) {
      const parent = items.find((i) =>
        i.subItems?.some((s) => s.value === selectedSubItem.value)
      );
      setExpandedParent(parent?.value ?? null);
      if (!isNavigating) {
        setOpen(true);
      }
    }
  }, [selectedSubItem, items, isNavigating]);

  // Force visibility on first load for Safari
  // useEffect(() => {
  //   if (isSafari) {
  //     // Force a re-render to ensure visibility in Safari
  //     const timer = setTimeout(() => {
  //       setOpen(true);
  //       setTimeout(() => {
  //         if (!selectedSubItem) {
  //           setOpen(false);
  //         }
  //       }, 300);
  //     }, 100);

  //     return () => clearTimeout(timer);
  //   }
  //   return () => {}; // Return empty function for non-Safari browsers
  // }, [isSafari, selectedSubItem]);

  const resetNavigationState = () => {
    setTimeout(() => {
      setIsNavigating(false);
    }, 50);
  };

  const handleParentClick = (item: NavItem) => {
    setIsNavigating(true);
    onItemSelected(item);
    setOpen(false);
    if (item.subItems) {
      setExpandedParent((prev) => (prev === item.value ? null : item.value));
    }
    resetNavigationState();
  };

  const handleSubItemClick = (sub: NavItem, parent: NavItem) => {
    setIsNavigating(true);
    onSubItemSelected?.(sub, parent);
    resetNavigationState();
  };

  const defaultGetItemIcon = (icon: string) => {
    switch (icon) {
      case 'HomeWorkIcon':
        return <HomeWorkOutlinedIcon />;
      case 'InventoryBox':
        return <InventoryBox />;
      case 'Financials':
        return <PaidOutlinedIcon />;
      case 'RequestQuote':
        return <RequestQuoteOutlinedIcon />;
      case 'CheckList':
        return <FactCheckOutlinedIcon />;
      case 'Notes':
        return <CommentOutlinedIcon />;
      default:
        return <HomeWorkOutlinedIcon />;
    }
  };

  const renderIcon = getItemIcon || defaultGetItemIcon;

  const drawerRef = useRef<HTMLDivElement>(null);

  const paddingTop =
    config?.convergeEnv?.toString() === env.Dev ||
    config?.convergeEnv?.toString() === env.Staging ||
    config?.convergeEnv?.toString() === env.QA
      ? '12.9rem'
      : '11.4rem';

  return (
    <NavWrapper>
      <StyledDrawer
        variant="permanent"
        open={open}
        tabIndex={0}
        paddingTop={paddingTop}
        ref={drawerRef}
        ModalProps={{
          keepMounted: true,
          BackdropProps: { invisible: true },
        }}
        anchor="left"
        sx={{
          flexGrow: 1,
          overflow: 'visible !important',
          zIndex: 2000,
          position: 'fixed',
          ...(isSafari && {
            display: 'block',

            WebkitBackfaceVisibility: 'hidden',
          }),
        }}
        onBlur={(e) => {
          if (
            !e.currentTarget.contains(e.relatedTarget as Node) &&
            !isNavigating
          ) {
            setOpen(false);
          }
        }}
        onFocus={() => !isNavigating && setOpen(true)}
      >
        <List
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            paddingTop: 0,
            height: '100%',
            minWidth: '78px',
            // Hide scrollbar for Chrome, Safari and Opera
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            // Hide scrollbar for IE, Edge and Firefox
            '-ms-overflow-style': 'none', // IE and Edge
            'scrollbar-width': 'none', // Firefox
          }}
        >
          {items.map((item) => {
            const isSelected = selectedItem?.value === item.value;
            const isExpanded = expandedParent === item.value;

            return (
              <React.Fragment key={item.value}>
                <Box
                  className={`left-nav-item-wrapper ${
                    open ? 'nav-item-wrapper-open' : ''
                  }`}
                  onMouseEnter={() => {
                    if (!isNavigating) {
                      setOpen(true);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isNavigating) {
                      setOpen(false);
                    }
                  }}
                >
                  <ListItemButton
                    selected={isSelected}
                    className="item-button"
                    sx={{
                      minHeight: 48,
                      '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: '#08f0d1',
                        minWidth: '78px',
                        height: '78px',
                      },
                    }}
                    onClick={() => {
                      handleParentClick(item);
                      if (!item.subItems) {
                        setOpen(false);
                      }
                    }}
                  >
                    {item.icon && (
                      <ListItemIcon className="item-icon">
                        {renderIcon(item.icon)}
                      </ListItemIcon>
                    )}
                    {open && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: 600,
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                </Box>

                {item.subItems && (
                  <Collapse
                    in={isExpanded && open}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        // Hide scrollbar for Chrome, Safari and Opera
                        '&::-webkit-scrollbar': {
                          display: 'none',
                        },
                        // Hide scrollbar for IE, Edge and Firefox
                        '-ms-overflow-style': 'none', // IE and Edge
                        'scrollbar-width': 'none', // Firefox
                      }}
                    >
                      {item.subItems.map((sub) => {
                        const isSubSelected =
                          selectedSubItem?.value === sub.value;
                        return (
                          <ListItemButton
                            key={sub.value}
                            selected={isSubSelected}
                            sx={{
                              pl: open ? 4 : 2,
                              '&.Mui-selected, &.Mui-selected:hover': {
                                backgroundColor: '#08f0d1',
                                minWidth: '78px',
                                height: '78px',
                              },
                            }}
                            onClick={() => handleSubItemClick(sub, item)}
                          >
                            {sub.icon && (
                              <ListItemIcon>{sub.icon}</ListItemIcon>
                            )}
                            {open && <ListItemText primary={sub.label} />}
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </StyledDrawer>
    </NavWrapper>
  );
};
